import { GridSortItem, GridSortModel } from '@mui/x-data-grid-premium'
import { GripVertical, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { Option } from '~/@/components/inputs/selection/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { TableColumnAutocomplete, TableColumnOption } from './TableColumnAutocomplete'

interface SortRowProps {
  index: number
  item: GridSortItem
  columns: TableColumnOption[]
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
  onRemove: (index: number) => void
  disableFields: boolean
  tableId: string
}

function useSortDirectionOptions(): Option[] {
  const { t } = useTypedTranslation()
  return [
    { label: t('sort_direction.asc'), value: 'asc' },
    { label: t('sort_direction.desc'), value: 'desc' },
  ]
}

export function SortRow({
  index,
  item,
  columns,
  sortModel,
  onSortModelChange,
  onRemove,
  disableFields,
  tableId,
}: SortRowProps) {
  const { t } = useTypedTranslation()
  const [selectedColumn, setSelectedColumn] = useState<TableColumnOption | undefined>(
    columns.find((col) => col.id === item.field),
  )

  useEffect(() => {
    setSelectedColumn(columns.find((col) => col.id === item.field))
  }, [columns, item.field])

  const handleColumnChange = useCallback(
    (newColumn: TableColumnOption | undefined) => {
      setSelectedColumn(newColumn)

      if (!newColumn) {
        return
      }

      const updatedItems = [...sortModel]
      updatedItems[index] = {
        field: newColumn.id,
        sort: item.sort || 'asc',
      }

      onSortModelChange(updatedItems)
    },
    [index, item.sort, onSortModelChange, sortModel],
  )

  const handleDirectionChange = useCallback(
    (value: string | undefined) => {
      if (!value) {
        return
      }

      const updatedItems = [...sortModel]
      updatedItems[index] = {
        ...item,
        sort: value as 'asc' | 'desc',
      }

      onSortModelChange(updatedItems)
    },
    [index, item, onSortModelChange, sortModel],
  )

  const selectedDirection = item.sort

  return (
    <div className="flex items-center gap-2">
      {!disableFields && (
        <div className="flex h-9 w-6 shrink-0 cursor-grab items-center justify-center text-gray-6">
          <GripVertical className="size-4" />
        </div>
      )}
      <TableColumnAutocomplete
        className="w-[240px] shrink-0"
        columns={columns.map((col) => ({
          ...col,
          table: { id: tableId, name: '' },
        }))}
        disabled={disableFields}
        onChange={handleColumnChange}
        selectedColumn={
          selectedColumn ? { ...selectedColumn, table: { id: tableId, name: '' } } : undefined
        }
        showIcon
      />

      <Dropdown
        className="w-[160px] shrink-0"
        disabled={disableFields}
        onChange={handleDirectionChange}
        options={useSortDirectionOptions()}
        placeholder={t('sort.direction_placeholder')}
        value={selectedDirection}
      />

      <InputWrapper>
        <Button
          className="size-9 shrink-0 rounded p-0"
          disabled={disableFields}
          onClick={() => onRemove(index)}
          variant="outline"
        >
          <Trash2 className="size-3" />
        </Button>
      </InputWrapper>
    </div>
  )
}
